import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useQueryClient} from 'react-query';
import axios from '../axios/instance-calendar';
import {MAINTENANCE_QUERY_KEY} from '../utils/maintenanceApi';
import {claimTabIdentity} from './tabIdentity';
import {createQueryRefreshCoordinator} from './queryRefreshCoordinator';

const fallbackInterval = 90 * 1000;
const heartbeatTimeout = 60 * 1000;
const ackInterval = 60 * 1000;
const ackRetryDelay = 5 * 1000;
const initialRetry = 1000;
const maximumRetry = 30 * 1000;
const inactiveGracePeriod = 30 * 1000;
const authRecheckInterval = 60 * 1000;
const maxStreamAuthRetries = 3;

const realtimeTopics = [
  'calendar',
  'notifications',
  'maintenance',
  'attendance',
  'cashier',
  'profile',
  'patient-format',
];

const getStoredClinicId = () => {
  try {
    return JSON.parse(sessionStorage.getItem('selectedClinic') || 'null')?.id || '';
  } catch {
    return '';
  }
};

const getStoredUserId = () => {
  try {
    const account = JSON.parse(localStorage.getItem('name') || 'null');
    return String(account?.id || account?.userID || '');
  } catch {
    return '';
  }
};

const parseEvent = (block) => {
  const event = {type: 'message', data: ''};
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event.type = line.slice(6).trim();
    if (line.startsWith('data:')) event.data += `${line.slice(5).trim()}\n`;
  }
  if (!event.data) return null;
  try {
    event.data = JSON.parse(event.data);
    return event;
  } catch {
    return null;
  }
};

const readEvents = async (response, signal, onEvent) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let pending = '';
  try {
    while (!signal.aborted) {
      const {done, value} = await reader.read();
      if (done) return;
      pending += decoder.decode(value, {stream: true}).replace(/\r\n/g, '\n');
      let boundary = pending.indexOf('\n\n');
      while (boundary >= 0) {
        const event = parseEvent(pending.slice(0, boundary));
        pending = pending.slice(boundary + 2);
        if (event) onEvent(event);
        boundary = pending.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
};

const isUnauthorized = (error) => {
  const status = error?.response?.status || error?.status;
  return status === 401 || status === 403;
};

const RealtimeContext = createContext({
  connectionState: 'disconnected',
  subscribeCalendar: () => () => {},
});

export const useRealtimeState = () => useContext(RealtimeContext).connectionState;

export function useRealtimeCalendar({onInvalidate} = {}) {
  const {connectionState, subscribeCalendar} = useContext(RealtimeContext);
  const handlerRef = useRef(onInvalidate);
  useEffect(() => {
    handlerRef.current = onInvalidate;
  }, [onInvalidate]);
  useEffect(
    () => subscribeCalendar((message) => handlerRef.current?.(message)),
    [subscribeCalendar],
  );
  return {connectionState};
}

export function RealtimeProvider({children}) {
  const queryClient = useQueryClient();
  const [clinicId, setClinicId] = useState(getStoredClinicId);
  const [userId, setUserId] = useState(getStoredUserId);
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('user'));
  const [connectionState, setConnectionState] = useState('connecting');
  const calendarHandlers = useRef(new Set());
  // Invalidations that arrive while the tab is hidden are deferred here and
  // replayed once it is visible again, so returning to the tab refreshes only
  // what changed instead of every realtime-backed query.
  const deferred = useRef({calendar: false, status: new Set()});
  const statusRefresh = useMemo(() => createQueryRefreshCoordinator({
    queryClient,
    isActive: () => document.visibilityState === 'visible' && navigator.onLine &&
      getStoredClinicId() === clinicId && getStoredUserId() === userId &&
      localStorage.getItem('user') === authToken,
  }), [queryClient, clinicId, userId, authToken]);
  useEffect(() => () => statusRefresh.dispose(), [statusRefresh]);

  useEffect(() => {
    const syncClinic = () => {
      const next = getStoredClinicId();
      setClinicId((current) => (current === next ? current : next));
      setUserId(getStoredUserId());
      setAuthToken(localStorage.getItem('user'));
    };
    window.addEventListener('focus', syncClinic);
    window.addEventListener('storage', syncClinic);
    return () => {
      window.removeEventListener('focus', syncClinic);
      window.removeEventListener('storage', syncClinic);
    };
  }, []);

  const subscribeCalendar = useCallback((handler) => {
    calendarHandlers.current.add(handler);
    return () => {
      calendarHandlers.current.delete(handler);
    };
  }, []);

  const emitCalendar = useCallback(
    (message = {}) => {
      if (document.visibilityState !== 'visible' || !navigator.onLine) {
        deferred.current.calendar = true;
        return;
      }
      calendarHandlers.current.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error('Calendar realtime handler failed:', error);
        }
      });
      const scopes = message.scopes || [];
      const broad = Boolean(message.reason) || scopes.length === 0;
      const detailAffected =
        message.parts?.includes('detail') ||
        scopes.some((scope) => scope.parts?.includes('detail'));
      if (!broad && !detailAffected) return;
      const userId = getStoredUserId();
      statusRefresh.invalidate({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            (message.since === undefined || query.state.dataUpdatedAt < message.since) &&
            key[0] === 'appointment-detail' &&
            String(key[1]) === String(clinicId) &&
            String(key[2]) === String(userId)
          );
        },
      });
    },
    [clinicId, statusRefresh],
  );

  const emitStatus = useCallback(
    // `fetchedBefore` limits a reconnect reconcile to data that could have
    // missed an event; anything fetched after the connection began is current.
    (type, fetchedBefore) => {
      if (document.visibilityState !== 'visible' || !navigator.onLine) {
        deferred.current.status.add(type);
        return;
      }
      const userId = getStoredUserId();
      const stale = (query) => fetchedBefore === undefined || query.state.dataUpdatedAt < fetchedBefore;
      const invalidate = (prefix) => statusRefresh.invalidate({
        predicate: (query) => {
          const key = query.queryKey;
          if (key[0] !== prefix || !stale(query)) return false;
          if (prefix === 'notifications') return String(key[2]) === userId && String(key[3]) === clinicId;
          if (prefix === 'patient-medical-record') return String(key[1]) === clinicId;
          return String(key[1]) === userId && String(key[2]) === clinicId;
        },
      });
      switch (type) {
        case 'notifications.invalidate':
          invalidate('notifications');
          window.dispatchEvent(new CustomEvent('fdc-notifications-changed'));
          break;
        case 'maintenance.invalidate':
          statusRefresh.invalidate({queryKey: MAINTENANCE_QUERY_KEY, predicate: stale});
          break;
        case 'attendance.invalidate':
          invalidate('attendance-status');
          break;
        case 'cashier.invalidate':
          invalidate('cashier-status');
          break;
        case 'profile.invalidate':
          invalidate('profile');
          break;
        case 'patient-format.invalidate':
          invalidate('patient-medical-record');
          break;
        default:
          break;
      }
    },
    [clinicId, statusRefresh],
  );

  const reconcileStatus = useCallback((fetchedBefore) => {
    if (document.visibilityState !== 'visible' || !navigator.onLine) return;
    deferred.current.status.clear();
    realtimeTopics.filter((topic) => topic !== 'calendar').forEach((topic) => emitStatus(`${topic}.invalidate`, fetchedBefore));
  }, [emitStatus]);

  const replayDeferred = useCallback(() => {
    if (document.visibilityState !== 'visible' || !navigator.onLine) return;
    const {calendar, status} = deferred.current;
    deferred.current = {calendar: false, status: new Set()};
    if (calendar) emitCalendar({reason: 'calendar-reconcile'});
    status.forEach((type) => emitStatus(type));
  }, [emitCalendar, emitStatus]);

  useEffect(() => {
    if (!clinicId || !userId || !authToken) {
      setConnectionState('disconnected');
      return undefined;
    }
    const baseUrl = process.env.REACT_APP_FAST_API_REALTIME;
    const lastFallback = new Map(realtimeTopics.map((topic) => [topic, Date.now()]));
    const fallbackStatus = () => {
      if (document.visibilityState !== 'visible' || !navigator.onLine) return;
      const now = Date.now();
      realtimeTopics.filter((topic) => topic !== 'calendar').forEach((topic) => {
        const interval = topic === 'notifications' ? 30000 : topic === 'maintenance' ? 60000 : fallbackInterval;
        if (now - (lastFallback.get(topic) || 0) >= interval) {
          lastFallback.set(topic, now);
          emitStatus(`${topic}.invalidate`);
        }
      });
    };
    if (!baseUrl) {
      setConnectionState('fallback');
      let lastCalendarFallback = Date.now();
      const fallback = setInterval(() => {
        fallbackStatus();
        if (Date.now() - lastCalendarFallback >= fallbackInterval) {
          emitCalendar({reason: 'calendar-reconcile'});
          lastCalendarFallback = Date.now();
        }
      }, 30000);
      const resume = () => { emitCalendar({reason: 'calendar-reconcile'}); reconcileStatus(); };
      document.addEventListener('visibilitychange', resume);
      window.addEventListener('online', resume);
      return () => {
        clearInterval(fallback);
        document.removeEventListener('visibilitychange', resume);
        window.removeEventListener('online', resume);
      };
    }

    let tabId;
    const identityController = new AbortController();
    let stopped = false;
    let activeController;
    let retryTimer;
    let fallbackTimer;
    let watchdogTimer;
    let ackTimer;
    let ackController;
    let ackRetryTimer;
    let retryDelay = initialRetry;
    let ready = false;
    let inactiveTimer;
    let retryAfter = 0;
    let streamAuthFailures = 0;
    let connection = null;

    const reconcile = () => {
      deferred.current.calendar = false;
      emitCalendar({reason: 'calendar-reconcile'});
      reconcileStatus();
    };

    const scheduleFallback = () => {
      if (fallbackTimer || stopped) return;
      let lastCalendarFallback = Date.now();
      fallbackTimer = setInterval(() => {
        if (ready) return;
        fallbackStatus();
        if (Date.now() - lastCalendarFallback >= fallbackInterval) {
          emitCalendar({reason: 'calendar-reconcile'});
          lastCalendarFallback = Date.now();
        }
      }, 30000);
    };

    const scheduleRetry = () => {
      if (stopped || retryTimer || document.visibilityState === 'hidden' || !navigator.onLine) return;
      const jitter = Math.floor(Math.random() * retryDelay * 0.25);
      retryTimer = setTimeout(() => {
        retryTimer = null;
        connect();
      }, Math.max(retryDelay + jitter, retryAfter));
      retryAfter = 0;
      retryDelay = Math.min(retryDelay * 2, maximumRetry);
    };

    const stopAck = () => {
      clearInterval(ackTimer);
      ackTimer = null;
      clearTimeout(ackRetryTimer);
      ackRetryTimer = null;
      connection = null;
      ackController?.abort();
      ackController = null;
    };

    // One quick retry keeps a single dropped acknowledgement from letting the
    // server-side lease lapse before the next scheduled acknowledgement.
    const retryAcknowledge = (target) => {
      if (stopped || ackRetryTimer || connection !== target) return;
      ackRetryTimer = setTimeout(() => {
        ackRetryTimer = null;
        acknowledge();
      }, ackRetryDelay);
    };

    const acknowledge = async () => {
      // Hidden tabs keep acknowledging: the stream is still open during the
      // inactive grace period, and a skipped acknowledgement lets the lease lapse.
      if (!connection || ackController || !navigator.onLine) return;
      const target = connection;
      const controller = new AbortController();
      ackController = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(new URL('/calendar/ack', baseUrl).toString(), {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          body: JSON.stringify({connectionId: target.id, control: target.control}),
          signal: controller.signal,
        });
        if (connection === target && !stopped && [401, 403, 410].includes(response.status)) target.controller.abort();
        else if (response.status < 200 || response.status >= 300) retryAcknowledge(target);
      } catch {
        // Lease expiry recovers acknowledgements that also fail on retry.
        retryAcknowledge(target);
      } finally {
        clearTimeout(timeout);
        if (ackController === controller) ackController = null;
      }
    };

    const connect = async () => {
      if (activeController || !tabId) return;
      if (stopped || document.visibilityState === 'hidden' || !navigator.onLine) {
        setConnectionState('disconnected');
        scheduleFallback();
        return;
      }

      ready = false;
      stopAck();
      const connectStartedAt = Date.now();
      setConnectionState('connecting');
      activeController = new AbortController();
      const controller = activeController;
      let ticketAuthFailed = false;
      let receivedStreamResponse = false;

      const resetWatchdog = () => {
        clearTimeout(watchdogTimer);
        watchdogTimer = setTimeout(() => controller.abort(), heartbeatTimeout);
      };

      watchdogTimer = setTimeout(() => controller.abort(), 10000);
      try {
        let ticketResponse;
        try {
          ticketResponse = await axios.post(
            '/calendar/realtime-ticket',
            {clinicId, topics: realtimeTopics, tab: tabId, clientAck: true},
            {suppressMaintenanceToast: true, signal: controller.signal},
          );
        } catch (error) {
          ticketAuthFailed = error?.response?.status === 401;
          if (isUnauthorized(error)) retryDelay = Math.max(retryDelay, authRecheckInterval);
          if (error?.response?.status === 429) {
            retryAfter = Math.max(1000, Number(error.response.headers?.['retry-after']) * 1000 || 60000);
          }
          throw error;
        }
        const streamUrl = new URL('/calendar/events', baseUrl).toString();
        const response = await fetch(streamUrl, {
          method: 'GET',
          headers: {Authorization: `Bearer ${ticketResponse.data.ticket}`} ,
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          signal: controller.signal,
        });

        if (response.status === 401 || response.status === 403) {
          streamAuthFailures += 1;
          if (streamAuthFailures > maxStreamAuthRetries) retryDelay = authRecheckInterval;
          throw new Error(`Calendar realtime authorization failed (${response.status})`);
        }
        if (response.status === 429) {
          retryAfter = Math.max(1000, Number(response.headers.get('Retry-After')) * 1000 || 60000);
        }
        if (!response.ok || !response.body) {
          throw new Error(`Calendar realtime unavailable (${response.status})`);
        }

        receivedStreamResponse = true;
        resetWatchdog();
        await readEvents(response, controller.signal, (event) => {
          if (event.type === 'ready') {
            ready = true;
            realtimeTopics.forEach((topic) => lastFallback.set(topic, Date.now()));
            retryDelay = initialRetry;
            streamAuthFailures = 0;
            clearInterval(fallbackTimer);
            fallbackTimer = null;
            resetWatchdog();
            connection = {id: event.data.connectionEpoch, control: event.data.control, controller};
            if (connection.id && connection.control && !ackTimer) {
              acknowledge();
              ackTimer = setInterval(acknowledge, ackInterval);
            }
            setConnectionState('connected');
            deferred.current.calendar = false;
            emitCalendar({reason: 'calendar-reconnected', since: connectStartedAt});
            reconcileStatus(connectStartedAt);
          } else if (event.type === 'calendar.invalidate') {
            emitCalendar(event.data);
          } else if (event.type === 'calendar.reset') {
            ready = false;
            stopAck();
            controller.abort();
            setConnectionState('disconnected');
          } else if (event.type === 'heartbeat') {
            resetWatchdog();
          } else if (typeof event.type === 'string' && event.type.endsWith('.invalidate')) {
            emitStatus(event.type);
          }
        });
      } catch (error) {
        if (!stopped && !controller.signal.aborted) {
          console.error('Calendar realtime connection failed:', error);
        }
      } finally {
        if (activeController === controller) activeController = null;
        clearTimeout(watchdogTimer);
        stopAck();
        if (!stopped) {
          ready = false;
          setConnectionState('disconnected');
          scheduleFallback();
          if (!ticketAuthFailed || receivedStreamResponse) scheduleRetry();
        }
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      clearTimeout(inactiveTimer);
      inactiveTimer = null;
      if (ready) {
        // The stream stayed open while hidden, so nothing was missed: confirm
        // the lease now and refresh only what was invalidated meanwhile.
        acknowledge();
        replayDeferred();
      }
      if (!ready && !activeController) {
        clearTimeout(retryTimer);
        retryTimer = null;
        connect();
      }
    };
    const onOnline = () => {
      clearTimeout(inactiveTimer);
      inactiveTimer = null;
      if (ready) reconcile();
      if (!ready && !activeController) {
        clearTimeout(retryTimer);
        retryTimer = null;
        connect();
      }
    };
    const suspend = () => {
      clearTimeout(retryTimer);
      retryTimer = null;
      if (inactiveTimer) return;
      inactiveTimer = setTimeout(() => {
        inactiveTimer = null;
        if (document.visibilityState === 'hidden' || !navigator.onLine) {
          activeController?.abort();
          setConnectionState('disconnected');
        }
      }, inactiveGracePeriod);
    };
    const onHidden = () => {
      if (document.visibilityState === 'hidden') suspend();
    };
    const onOffline = suspend;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') onHidden();
      else onVisible();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('pagehide', suspend);
    scheduleFallback();
    claimTabIdentity(identityController.signal).then((id) => {
      if (stopped) return;
      tabId = id;
      connect();
    });

    return () => {
      stopped = true;
      identityController.abort();
      stopAck();
      clearInterval(fallbackTimer);
      clearInterval(ackTimer);
      clearTimeout(retryTimer);
      clearTimeout(watchdogTimer);
      clearTimeout(inactiveTimer);
      activeController?.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('pagehide', suspend);
    };
  }, [clinicId, userId, authToken, emitCalendar, emitStatus, reconcileStatus, replayDeferred]);

  const value = useMemo(
    () => ({connectionState, subscribeCalendar}),
    [connectionState, subscribeCalendar],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
